import classNames from 'classnames';
import {
	CleanseAddress as CleanseAddressModule,
	Data,
	Modal as ModalModule,
	UI,
	Utils,
} from 'common';
import MeterContactInfoModal from 'common/src/code/settings/common/MeterContactInfoModal.component';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import sum from 'lodash/sum';
import { ProgressSpinner } from 'primereact/progressspinner';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import {
	CostDetails as CostDetailsEnum,
	FeeType,
} from '../shared/CostDetails.enum';
import FeeDetails from '../shared/FeeDetails';
import FeeUtil from '../shared/FeeUtil';
import HistoryCommunication from '../utils/HistoryCommunication';
import HistoryUtil from '../utils/Util';
import PrintContent from './PrintContent.component';
import ReturnLabelPreviewModal from './ReturnLabelPreviewModal.component';
import StaticRateData from './StaticRateData.component';

const {
	CalculatedData,
	Carrier,
	Capability,
	DeliveryConfirmation,
	ServiceDataHelper,
	ServiceType,
	PackageType,
} = Data;
const { withLayoutAndModalContexts, Session, Util, Validation } = Utils;
const { Modal, ModalButtonActionType } = ModalModule;
const {
	AddressFieldWithLabel,
	InsuranceFieldWithLabel,
	ServiceFieldWithLabel,
	Spinner,
	WrappedInputTextareaWithLabel,
	WrappedInputTextWithLabel,
	WrappedDropdownWithLabel,
	WrappedRadioButton,
	WrappedCheckbox,
	WeightFieldWithLabel,
	DimensionsFieldWithLabel,
	WrappedTooltip,
	TrackingFieldWithLabel,
} = UI;
const { CleanseAddressStatus } = CleanseAddressModule;
const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

const EmailReturnLabelOption = {
	Stamps: 'stamps',
	Customer: 'customer',
};

class CreateReturnLabelModal extends Component {
	constructor(props) {
		super(props);

		const {
			customerDetails: { email: userEmail },
		} = Session.getAccountInfo();

		this.state = {
			carrier: null,
			returnLabelFormView: true,
			fromAddress: {},
			toAddress: {},
			cleanseStatus: {
				fromAddress: CleanseAddressStatus.NotCleansed,
				toAddress: CleanseAddressStatus.NotCleansed,
			},
			cleanseMessage: {
				fromAddress: '',
				toAddress: '',
			},
			triggerCleanseAddress: {
				fromAddress: false,
				toAddress: false,
			},
			weight: 0,
			insureFor: 0,
			tracking: null,
			rmaMemo: '',
			costCode: 0,
			emailReturnLabelOption: EmailReturnLabelOption.Stamps,
			recipientEmail: '',
			notes: '',
			sendCopy: false,
			cost: {
				service: 0,
				tracking: 0,
				insureFor: 0,
			},
			isStaticRate: false,
			validationErrors: {
				// 0 Non-Validated, 1 Validated, -1 Invalid
				fromAddress: 0,
				toAddress: 0,
				weight: 0,
				service: 0,
				recipientEmail: 0,
			},

			service: null,
			serviceType: null,
			packageType: null,
			serviceRates: [],
			selectedServiceRate: null,
			insuranceOption: null,
			isLoading: false,
			loadingMessage: '',

			// in History, we only support USPS and UPS for now
			carriers: CalculatedData.allCarriers.filter(
				({ value }) => value === Carrier.USPS || value === Carrier.UPS
			),

			shipDate: '',
			weightOz: 0,
			weightLb: 0,
			dimensions: {
				height: 0,
				width: 0,
				length: 0,
			},

			userEmail,
			isTrackingDisabled: false,
			labelDetails: {},
		};

		// debounce the calls to updateServiceRates especailly when user changes weight
		// by continuously clicking the up and/or down-arrow triggers
		this.updateServiceRates = debounce(this.updateServiceRates, 500);
	}

	componentDidMount() {
		const { data } = this.props;
		const { selectedPrints } = data || {};
		const selectedPrint = isArray(selectedPrints) ? selectedPrints[0] : {};
		const printDetails = isObject(selectedPrint?.printDetails)
			? selectedPrint.printDetails
			: {};
		this.fetchLabelDetails(printDetails.labelId);
		this.setState({ carrier: printDetails.carrier });

		if (printDetails.eligibleForReturnLabel === true) {
			// use `to_address` for "From:" and `return_address` for "To:" for creating a return label
			const fromAddress = isObject(printDetails.toAddress)
				? printDetails.toAddress
				: {};
			const toAddress = isObject(printDetails.returnAddress)
				? printDetails.returnAddress
				: {};

			this.setState(
				{
					fromAddress,
					toAddress,
					cleanseStatus: {
						fromAddress: isObject(fromAddress)
							? CleanseAddressStatus.Cleansed
							: CleanseAddressStatus.NotCleansed,
						toAddress: isObject(toAddress)
							? CleanseAddressStatus.Cleansed
							: CleanseAddressStatus.NotCleansed,
					},
					weight:
						isNumber(printDetails.weightPounds) &&
						isNumber(printDetails.weightOunces)
							? 16 * printDetails.weightPounds + printDetails.weightOunces
							: 0,
					shipDate: printDetails.shipDate || '',
				},
				this.updateServiceRates
			);
		} else {
			this.updateServiceRates();
		}
	}

	/**
	 * This method returns the base of the payload object for rates and labels apis.
	 * @returns {object}
	 */
	buildBasePayload = () => {
		const {
			weight,
			insureFor,
			fromAddress,
			toAddress,
			serviceType,
			packageType,
			insuranceOption,
			dimensions,
			tracking,
		} = this.state;

		return {
			from_address: HistoryUtil.buildAddressForSERA(fromAddress),
			to_address: HistoryUtil.buildAddressForSERA(toAddress),
			service_type: serviceType,
			package: {
				packaging_type:
					packageType === PackageType.DropOffNoLabelNeeded ||
					packageType === PackageType.PrintAtHomeLabel
						? PackageType.Package
						: packageType,
				weight,
				weight_unit: 'ounce',
				height: dimensions.height,
				length: dimensions.length,
				width: dimensions.width,
			},
			insurance: isObject(insuranceOption)
				? {
						insurance_provider: insuranceOption.insuranceProvider,
						insured_value: {
							amount: insureFor,
							currency: insuranceOption.currency,
						},
					}
				: null,
			delivery_confirmation_type:
				tracking === DeliveryConfirmation.None
					? null
					: DeliveryConfirmation.Tracking,
			ship_date: new Date().toISOString().split('T')[0],
			is_return_label: true,
			advanced_options: {
				no_label: {
					is_drop_off: packageType === PackageType.DropOffNoLabelNeeded,
					is_prepackaged: true,
				},
			},
		};
	};

	/**
	 * Return the payload object of rates api.
	 * @param {boolean} forAll `true` to get all service rates.
	 * @returns {object}
	 */
	buildGetRatesPayload = forAll => {
		const basePayload = this.buildBasePayload();
		const isConsolidatedReturnOption =
			Session.hasCapability(Capability.UspsReturnServicePrepaid) ||
			Session.hasCapability(Capability.UpsReturns);
		const canPrintInstaLabel =
			!Session.hasCapability(Capability.UspsReturnServicePrepaid) &&
			Session.hasCapability(Capability.Instalabel) &&
			!Session.isFreemiumUser();

		if (forAll === true) {
			return {
				...basePayload,

				// override
				service_type: null,
				package: {
					...basePayload.package,
					packaging_type:
						isConsolidatedReturnOption || canPrintInstaLabel ? 'package' : null,
				},
				delivery_confirmation_type: null,
			};
		}

		return basePayload;
	};

	/**
	 * Return the payload object of labels api.
	 * @returns {object}
	 */
	buildLabelsPayload = () => {
		const basePayload = this.buildBasePayload();
		const {
			emailReturnLabelOption,
			recipientEmail,
			rmaMemo,
			notes,
			sendCopy,
			costCode,
		} = this.state;
		const { customerDetails } = Session.getAccountInfo();

		return {
			...basePayload,

			/**
			 * Only certain label are allowed to email:
			 * 1) Return label (no POUR)
			 * 2) DHLExpress
			 * 3) Pay-on-Use return
			 * 4) InstaLabel
			 */
			email_label:
				emailReturnLabelOption === EmailReturnLabelOption.Stamps
					? {
							email: recipientEmail,
							email_notes: notes,
							bcc_email: sendCopy ? customerDetails?.email : null,
						}
					: null,
			references: {
				printed_message1: rmaMemo,
				cost_code_id: costCode,
			},
			label_options: {
				label_format: 'png',
			},
		};
	};

	generateLabel = async () => {
		const { modalContext } = this.props;
		const payload = this.buildLabelsPayload();
		const getLabelsResponse = await HistoryCommunication.createLabels(payload);

		if (!HistoryCommunication.isSuccessful(getLabelsResponse)) {
			const { errorCode, errorMessage } =
				this.parseErrorResponse(getLabelsResponse);

			modalContext.closeAll(() => {
				modalContext.showError({
					data: {
						// display the error code
						code: isNumber(errorCode) && !isNaN(errorCode) ? errorCode : null,
						title: !isNumber(errorCode) ? 'Error' : null,
						description: `<p>${errorMessage}</p>`,
						primaryButtonText: 'OK',
						handleType: 'custom',
					},
				});
			});

			return null;
		}

		const { data } = getLabelsResponse;
		const labels = isArray(data?.labels) ? data.labels : [];
		const label = labels.length === 1 ? labels[0] : {};

		// update postage balance
		await Session.loadBalance();

		return label.href;
	};

	/**
	 * Make a call to getLabelDetails API to fetch data.
	 * @param {string} labelId
	 * @return {object}
	 */
	fetchLabelDetails = async labelId => {
		const { modalContext } = this.props;
		if (labelId !== undefined) {
			const response = await HistoryCommunication.getLabelDetails(labelId);

			if (HistoryCommunication.isSuccessful(response)) {
				const labelData = Util.toCamelCase(response.data);
				const labelDetails = labelData?.labelDetails || {};

				this.setState({
					dimensions: {
						length: labelDetails.package.length,
						width: labelDetails.package.width,
						height: labelDetails.package.height,
					},
					labelDetails,
				});
			} else {
				modalContext.showError({ response });
			}
		}
	};

	parseErrorResponse = response =>
		isObject(response.error)
			? response.error
			: {
					errorMessage: response.ErrorDescription,
				};

	/**
	 * Launch client's email application and populate the email content.
	 * @param {string} imageURL The label image URL to be included in the email body.
	 */

	launchEmail = imageURL => {
		const { carrier, fromAddress, recipientEmail, notes } = this.state;
		const { customerDetails } = Session.getAccountInfo();
		const { address } = isObject(customerDetails) ? customerDetails : {};
		const sender = `${address.name}${
			isString(customerDetails.email) && customerDetails.email !== ''
				? ` (${customerDetails.email})`
				: ''
		}`;

		const footer = {
			stamps: [
				`Print official USPS postage from your own printer with Stamps.com, `,
				'and eliminate trips to the Post Office forever! Sign up at ',
				'http://www.stamps.com/freeoffer and receive our ',
				'special introductory offer. Try it risk free today!',
			].join('\n'),
		};

		const emailTemplate = {
			subject: 'Return Label',
			body: [
				`${sender} has sent a prepaid, printable return label to use when returning your merchandise.`,
				'',
				'------------',
				'',
				`"${notes}"`,
				`- ${fromAddress.name}`,
				'',
				'Copy and paste the following link into your web browser.',
				`${imageURL}`,
				'\n',
				'================================================================',
				`This email is provided by Stamps.com. For questions about this return `,
				`shipping label, please contact ${sender} or ${
					carrier === Carrier.USPS
						? 'the U.S. Postal Service'
						: ServiceDataHelper.getCarrierDisplayName(carrier)
				}.`,
				'',
				`${footer.stamps}`,
			].join('\n'),
		};

		// launch email application
		document.location = `mailto:${recipientEmail}?subject=${
			emailTemplate.subject
		}&body=${encodeURIComponent(emailTemplate.body)}`;
	};

	/**
	 * Make a call to get new rates when changing:
	 * - from and to addresses
	 * - carrier
	 * - weight
	 */
	updateServiceRates = async () => {
		const { carrier, cleanseStatus, serviceRates } = this.state;
		const isCarrierDropdownHidden = this.hideCarrierDropdown();

		if (
			cleanseStatus.fromAddress === CleanseAddressStatus.Cleansed &&
			cleanseStatus.toAddress === CleanseAddressStatus.Cleansed
		) {
			const getRatesParams = this.buildGetRatesPayload(true); // `true` to fetch all service rates
			const getRatesResponse = await HistoryCommunication.getRates(
				getRatesParams,
				isCarrierDropdownHidden ? null : carrier
			);

			if (HistoryCommunication.isSuccessful(getRatesResponse)) {
				this.setState({
					serviceRates: StaticRateData.processRates(getRatesResponse.data),
				});
			}
		} else if (serviceRates.length > 0) {
			this.setState({
				serviceRates: [],
			});
		}
	};

	updateSelectedServiceRate = async () => {
		const { carrier, service, isStaticRate } = this.state;
		const isCarrierDropdownHidden = this.hideCarrierDropdown();

		if (isString(service)) {
			const getRatesParams = this.buildGetRatesPayload();
			const getRatesResponse = await HistoryCommunication.getRates(
				getRatesParams,
				isCarrierDropdownHidden ? null : carrier
			);

			if (HistoryCommunication.isSuccessful(getRatesResponse)) {
				const selectedServiceRate = StaticRateData.processRates(
					getRatesResponse.data
				);

				const staticData = selectedServiceRate.filter(rate => {
					if (
						rate?.serviceType === ServiceType.UspsPayOnUseReturn ||
						rate?.serviceType === ServiceType.UpsGround
					) {
						return rate?.packageName === service;
					} else {
						const val = `${`${rate?.serviceTitle} - ${rate?.packageName}`}`;
						return val === service;
					}
				})[0];

				if (isStaticRate) {
					this.setState(
						{
							selectedServiceRate: staticData,
							carrier: staticData.carrier,
						},
						this.updateCost
						// update tracking
					);
				} else {
					this.setState(
						{
							selectedServiceRate:
								selectedServiceRate.length === 1
									? selectedServiceRate[0]
									: null,
						},
						this.updateCost
					);
				}
			}
		} else {
			this.updateCost();
		}
	};

	/**
	 * Update cost(s) when getting a newer rate.
	 */
	updateCost = async () => {
		const { tracking, selectedServiceRate } = this.state;

		if (isObject(selectedServiceRate)) {
			const costDetails = isArray(selectedServiceRate.shipmentCost?.costDetails)
				? selectedServiceRate.shipmentCost.costDetails
				: [];
			const serviceCostDetail = find(costDetails, ['feeType', 'service']);
			const trackingCostDetail = find(costDetails, ['feeType', 'tracking']);
			const insuranceCostDetail = find(costDetails, ['feeType', 'insurance']);

			this.setState({
				cost: {
					insureFor: isNumber(insuranceCostDetail?.amount)
						? insuranceCostDetail.amount
						: 0,
					service: isNumber(serviceCostDetail?.amount)
						? serviceCostDetail.amount
						: 0,
					tracking:
						isString(tracking) && isNumber(trackingCostDetail?.amount)
							? trackingCostDetail.amount
							: 0,
				},
			});
		} else {
			this.setState({
				cost: {
					service: 0,
					tracking: 0,
					insureFor: 0,
				},

				// reset the service if the selected service rate is not available
				service: null,
			});
		}
	};

	/**
	 * Check if form is valid and updates states accordingly
	 * @returns {bool}
	 */
	isFormValid = () => {
		const {
			cleanseStatus,
			emailReturnLabelOption,
			weight,
			service,
			recipientEmail,
			returnLabelFormView,
		} = this.state;

		const newValidationErrors =
			returnLabelFormView === true
				? {
						fromAddress:
							cleanseStatus.fromAddress === CleanseAddressStatus.Cleansed
								? 1
								: -1,
						toAddress:
							cleanseStatus.toAddress === CleanseAddressStatus.Cleansed
								? 1
								: -1,
						weight: weight > 0 ? 1 : -1,
						service: service ? 1 : -1,
					}
				: {
						// email is not required if "Use my own email application" option is selected
						recipientEmail:
							emailReturnLabelOption === EmailReturnLabelOption.Customer ||
							(recipientEmail &&
								recipientEmail !== '' &&
								Validation.isValidEmail(recipientEmail))
								? 1
								: -1,
					};

		this.setState({
			validationErrors: newValidationErrors,
		});

		return Object.values(newValidationErrors).every(value => value === 1);
	};

	onAddressChange = ({ name }) => {
		const { cleanseStatus } = this.state;

		this.setState({
			cleanseStatus: {
				...cleanseStatus,
				[name]: CleanseAddressModule.NotCleansed,
			},
		});
	};

	onAddressCleansed = response => {
		const { address, name = '' } = response;

		// override empty address message from CleanseAddress component
		const emptyAddressMessage =
			name === 'fromAddress'
				? 'Return From address is required.'
				: 'Return To address is required.';

		this.setState(
			({
				cleanseStatus,
				cleanseMessage,
				fromAddress,
				toAddress,
				validationErrors,
				triggerCleanseAddress,
			}) => ({
				fromAddress:
					name === 'fromAddress' &&
					response.cleanseStatus === CleanseAddressStatus.Cleansed
						? address
						: fromAddress,
				toAddress:
					name === 'toAddress' &&
					response.cleanseStatus === CleanseAddressStatus.Cleansed
						? address
						: toAddress,
				cleanseStatus: {
					...cleanseStatus,
					[name]: response.cleanseStatus,
				},
				cleanseMessage: {
					...cleanseMessage,
					[name]:
						response.cleanseStatus === CleanseAddressStatus.EmptyAddress
							? emptyAddressMessage
							: response.cleanseMessage,
				},
				triggerCleanseAddress: {
					...triggerCleanseAddress,
					[name]: false,
				},
				validationErrors: {
					...validationErrors,
					[name]:
						response.cleanseStatus === CleanseAddressStatus.Cleansed ? 1 : -1,
				},
			}),
			this.updateServiceRates
		);
	};

	onChange = ({ target }) => {
		const { name, value, checked, type } = target;
		const { emailReturnLabelOption } = this.state;

		this.setState({
			[name]: type === 'checkbox' ? checked : value,
			loadingMessage:
				emailReturnLabelOption === EmailReturnLabelOption.Stamps
					? 'Sending return label...'
					: 'Creating return label...',
		});
	};

	/**
	 * Reset all inputs when selecting a new carrier option, and make a rates api call.
	 */
	onCarrierChange = ({ target }) => {
		const { value } = target;

		this.setState(
			{
				carrier: value,
				service: null,
				serviceType: null,
				packageType: null,
				tracking: DeliveryConfirmation.None,
				cost: {
					service: 0,
					tracking: 0,
					insureFor: 0,
				},
			},
			this.updateServiceRates
		);
	};

	/**
	 * Update service, cost and tracking when selecting a service.
	 */
	onServiceChange = ({ value, service }) => {
		const { serviceType, packageType, isStaticRate, carrier } = service || {};
		const { serviceRates, validationErrors } = this.state;

		this.setState(
			{
				carrier,
				service: value,
				selectedServiceRate: find(serviceRates, ['value', value]),
				serviceType,
				packageType,
				isStaticRate,
				validationErrors: {
					...validationErrors,

					// reset service validation error if it was invalid prior to the change
					service:
						validationErrors.service === -1 ? 1 : validationErrors.service,
				},
				insuranceOption: CalculatedData.getInsuranceOption(
					serviceType,
					packageType
				),
				isTrackingDisabled: false,
			},
			() => {
				this.updateSelectedServiceRate();
			}
		);
	};

	onInsureForChange = value => {
		const { service } = this.state;

		this.setState(
			{
				insureFor: value,
			},
			() => {
				/**
				 * Dev note:
				 * Make a rates api call only when a service has been selected, otherwise
				 * it's pointless to make the call as it will not return the insurance
				 * cost back.
				 */
				if (isString(service)) {
					this.updateServiceRates();
				}
			}
		);
	};

	onTrackingChange = ({ value, tracking }) => {
		const { cost, carrier } = this.state;
		const isConsolidatedReturnOption =
			Session.hasCapability(Capability.UspsReturnServicePrepaid) ||
			Session.hasCapability(Capability.UpsReturns);
		const canPrintInstaLabel =
			!Session.hasCapability(Capability.UspsReturnServicePrepaid) &&
			Session.hasCapability(Capability.Instalabel);
		const isConsolidatedUpsTracking =
			(isConsolidatedReturnOption || canPrintInstaLabel) &&
			carrier === Carrier.UPS &&
			value === DeliveryConfirmation.Tracking;

		if (isConsolidatedUpsTracking && tracking) {
			tracking.label = 'UPS Tracking';
		}

		this.setState({
			tracking: value,
			cost: {
				...cost,
				tracking: isObject(tracking) ? tracking.rate : 0,
			},
			isTrackingDisabled: isConsolidatedUpsTracking,
		});
	};

	/**
	 * Weight change handler. When changed, system makes two api calls to update:
	 * 1. all service rates, and
	 * 2. selected service cost.
	 */
	onWeightChange = ({ pounds, ounces }) => {
		this.setState(
			{
				weight: pounds * 16 + ounces,
				weightOz: ounces,
				weightLb: pounds,
			},
			this.updateServiceRates
		);

		this.onWeightBlur({ pounds, ounces });
	};

	// validate weight on blur
	onWeightBlur = ({ pounds, ounces }) => {
		this.setState(({ validationErrors }) => ({
			validationErrors: {
				...validationErrors,
				weight: pounds * 16 + ounces === 0 ? -1 : 1,
			},
		}));
	};

	onChangeDimension = ({ width, length, height }) => {
		this.setState({
			dimensions: {
				width,
				length,
				height,
			},
		});
	};

	/**
	 * Will check if there's any validation issue and will change the View if there is none
	 */
	onContinueButtonClick = () => {
		const { cleanseStatus } = this.state;

		if (
			cleanseStatus.fromAddress === CleanseAddressStatus.NotCleansed ||
			cleanseStatus.toAddress === CleanseAddressStatus.NotCleansed
		) {
			this.setState({
				triggerCleanseAddress: {
					fromAddress:
						cleanseStatus.fromAddress === CleanseAddressStatus.NotCleansed,
					toAddress:
						cleanseStatus.toAddress === CleanseAddressStatus.NotCleansed,
				},
			});
		} else if (this.isFormValid()) {
			this.setState({
				returnLabelFormView: false,
			});
		}
	};

	onPreviewButtonClick = () => {
		const {
			modalContext: { showModal },
		} = this.props;
		const { toAddress, notes } = this.state;

		showModal({
			component: ReturnLabelPreviewModal,
			data: {
				recipientName: toAddress?.name,
				notes,
			},
		});
	};

	onSendButtonClick = async () => {
		const {
			modalId,
			modalContext: { closeModal },
		} = this.props;
		const { emailReturnLabelOption } = this.state;

		if (this.isFormValid()) {
			this.setState({
				isLoading: true,
			});

			const imageURL = await this.generateLabel();

			if (
				isString(imageURL) &&
				emailReturnLabelOption === EmailReturnLabelOption.Customer
			) {
				this.launchEmail(imageURL);
			}

			this.setState({
				isLoading: false,
			});

			// close Return Label modal
			closeModal(modalId);
		}
	};

	/**
	 * Dev note:
	 * This is a temporary setup to support testing media-gen backend work, WEBAPPS-34273.
	 * Remove the method when WEBAPPS-34273 work is done.
	 */
	createMediaGen = async (sheet, startPosition, labelUrls) => {
		// make a request to mediagen
		const mediaGenResponse = await HistoryCommunication.createMediaGen({
			labelFormat: 'png',
			labelUrls,
			sheet,
			startPos: startPosition,
		});

		const { data: mediaData } = mediaGenResponse;
		const stitchedLabel = isArray(mediaData) ? mediaData : [];
		const stitchedLabelUrl = stitchedLabel.length === 1 ? stitchedLabel[0] : {};

		this.setState({
			imageURL: isString(stitchedLabelUrl.imageUrl)
				? stitchedLabelUrl.imageUrl
				: null,
		});
	};

	/**
	 * Dev note:
	 * This is a temporary setup to support testing media-gen backend work, WEBAPPS-34273.
	 * Remove the method when WEBAPPS-34273 work is done.
	 */
	onAfterPrint = () => {
		this.setState({
			isLoading: false,
			imageURL: null,
		});
	};

	renderCarrierItemTemplate = ({ displayName, value }) => {
		const carrierIconCls = value.replace(/_/g, '-');

		return (
			<div className="carrier-item">
				<div className="carrier-icon table-cell">
					<span className={carrierIconCls} />
				</div>
				<div className="carrier-title table-cell ellipsis">
					<span>{displayName}</span>
				</div>
			</div>
		);
	};

	/**
	 * Render service group item template: "<carrier icon> <service name> (<ETA>)".
	 * @param {object}
	 * @returns {ReactElement}
	 */
	renderServiceGroupItemTemplate = params => {
		const {
			carrier,
			serviceName,
			isFetching,
			serviceEta,
			serviceTooltip,
			packageType,
			serviceType,
		} = params;

		const carrierIconCls = carrier.replace(/_/g, '-');
		const tooltipTarget = `service-group-item-${serviceType}-${packageType}-tooltip`;

		return (
			<>
				<div className={classNames('service-group-item', tooltipTarget)}>
					<div className="carrier-icon table-cell">
						<span className={carrierIconCls} />
					</div>
					<div className="service-title table-cell">
						{/* show the spinner while system is making a call to fetch ETA */}
						{isFetching === true ? (
							<>
								<span>{serviceName}</span>
								<ProgressSpinner strokeWidth="2" animationDuration=".7s" />
							</>
						) : (
							<span>
								{serviceName} {serviceEta}
							</span>
						)}
					</div>
				</div>
				<WrappedTooltip tooltip={serviceTooltip} target={tooltipTarget} />
			</>
		);
	};

	hideCarrierDropdown = () => {
		const isUspsReturnServicePrepaid = Session.hasCapability(
				Capability.UspsReturnServicePrepaid
			),
			isInstaLabel = Session.hasCapability(Capability.Instalabel),
			hasAccessToMultipleCarriers = Session.hasAccessToMultipleCarriers(),
			isFreemiumUser = Session.isFreemiumUser();

		return (
			isUspsReturnServicePrepaid ||
			isInstaLabel ||
			hasAccessToMultipleCarriers !== true ||
			isFreemiumUser
		);
	};

	showDimension = () => {
		const returnLabelPackageStr = 'Drop-Off: No Label Needed';
		const printAtHomeStr = 'Print-at-Home Label';
		const printAtHomeStrUPS = 'Drop Off: Print-at-Home Label';
		const { data } = this.props;
		const { selectedPrints } = data || {};
		const { dimensions } = this.state;
		const { serviceType, packageType, service } = this.state;
		const isNoLabelDropOffSelected = service === returnLabelPackageStr;
		const isPrintAtHomeSelected =
			service === printAtHomeStr || service === printAtHomeStrUPS;
		const isConsolidated = packageType === 'USRETURN_Package';
		const isNonConsolidated =
			!isConsolidated && (isNoLabelDropOffSelected || isPrintAtHomeSelected);
		const isStandAlone = isEmpty(selectedPrints);
		const isEmptyOutBoundDimensions = !!(
			!isStandAlone &&
			dimensions.length + dimensions.width + dimensions.height === 0
		);

		if (serviceType === ServiceType.UpsGround) {
			return true;
		}

		if (isConsolidated) {
			return isStandAlone || !isEmptyOutBoundDimensions;
		}

		if (isNonConsolidated) {
			return false;
		}

		return false;
	};

	onEditUserEmailButtonClick = (callback = false) => {
		const {
			modalContext: { showModal },
		} = this.props;

		showModal({
			component: MeterContactInfoModal,
			data: {
				changeEmailCallback: newEmail => {
					this.setState({
						userEmail: newEmail,
					});

					if (callback) {
						callback();
					}
				},
			},
		});
	};

	renderFirstView = () => {
		const {
			layoutContext: { isMobile },
			data = {},
		} = this.props;

		const {
			carrier,
			carriers,
			cleanseMessage,
			service,
			serviceRates,
			fromAddress,
			toAddress,
			weight,
			insureFor,
			insuranceOption,
			tracking,
			rmaMemo,
			costCode,
			cost,
			validationErrors,
			triggerCleanseAddress,
			selectedServiceRate,
			dimensions,
			isStaticRate,
			serviceType,
			packageType,
			isTrackingDisabled,
			labelDetails = {},
		} = this.state;
		const hasCostCodesCapability = Session.hasCapability(Capability.CostCodes);
		const totalCost = sum(Object.values(cost));
		const { costCodeOptions, showUpgradeModal } = data;
		const mobileClassName = isMobile ? 'mobile-wrapper' : '';
		const insureForText =
			carrier === Carrier.UPS ? 'Declared Value:' : 'Insure For:';
		const isCarrierDropdownHidden = this.hideCarrierDropdown();
		const showReturnsDescription =
			isObject(selectedServiceRate) && isStaticRate === true;
		const isShowDimension = this.showDimension();
		const { shipmentCost = {} } = labelDetails;
		const { costDetails = [] } = shipmentCost;

		return (
			<div className="p-grid p-nogutter">
				<div className={`p-col-10 ${mobileClassName}`}>
					<AddressFieldWithLabel
						label="From:"
						isInputInvalid={validationErrors.fromAddress === -1}
						showErrorBelow={isMobile}
						errorMessage={cleanseMessage.fromAddress}
						uiElementConfig={{
							name: 'fromAddress',
							placeholder: "Your customer's address",
							address: fromAddress,
							onChange: this.onAddressChange,
							onCleansed: this.onAddressCleansed,
							triggerCleanseAddress: triggerCleanseAddress.fromAddress,
							dataqa: 'create-return-label-from-address',
						}}
					/>
				</div>
				<div className={`p-col-10 ${mobileClassName}`}>
					<AddressFieldWithLabel
						label="To:"
						isInputInvalid={validationErrors.toAddress === -1}
						showErrorBelow={isMobile}
						errorMessage={cleanseMessage.toAddress}
						uiElementConfig={{
							name: 'toAddress',
							placeholder: 'Your address',
							address: toAddress,
							onChange: this.onAddressChange,
							onCleansed: this.onAddressCleansed,
							triggerCleanseAddress: triggerCleanseAddress.toAddress,
							dataqa: 'create-return-label-to-address',
						}}
					/>
				</div>
				<div className="p-col-12">
					<hr />
				</div>
				<div className={`p-col-9 ${mobileClassName}`}>
					<WeightFieldWithLabel
						label="Weight:"
						isInputInvalid={validationErrors.weight === -1}
						showErrorBelow={isMobile}
						errorMessage="Weight cannot be zero"
						uiElementConfig={{
							onChange: this.onWeightChange,
							onBlur: this.onWeightBlur,
							value: weight,
							min: 0,
							weight: { pounds: Math.floor(weight / 16), ounces: weight % 16 },
							dataqa: 'create-return-label-weight',
						}}
					/>
				</div>
				{isShowDimension && (
					<DimensionsFieldWithLabel
						label="Dimensions:"
						uiElementConfig={{
							onChange: this.onChangeDimension,
							dimensions: {
								length: dimensions.length,
								width: dimensions.width,
								height: dimensions.height,
							},
							doValidation: false,
							dataqa: 'create-return-label-dimensions',
						}}
					/>
				)}
				{!isCarrierDropdownHidden && (
					<div className={`p-col-10 ${mobileClassName}`}>
						<WrappedDropdownWithLabel
							className={`p-col-12 ${mobileClassName}`}
							label="Carrier:"
							uiElementConfig={{
								name: 'carrier',
								placeholder: 'Any',
								options: carriers,
								optionLabel: 'displayName',
								optionValue: 'value',
								value: carrier,
								itemTemplate: this.renderCarrierItemTemplate,
								panelClassName: 'carrier-dropdown-panel',
								onChange: this.onCarrierChange,
								dataqa: 'return-label-carrier',
							}}
						/>
					</div>
				)}

				<div className={`p-col-10 ${mobileClassName}`}>
					<ServiceFieldWithLabel
						className={`p-col-12 ${mobileClassName}`}
						label="Service:"
						errorMessage="Service is required."
						isInputInvalid={validationErrors.service === -1}
						showErrorBelow={isMobile}
						uiElementConfig={{
							enableGrouping: true,
							name: 'service',
							placeholder: 'Select a Service',
							options: serviceRates,
							rateOptionValue: 'shipmentCost.totalAmount',
							value: service,
							groupItemTemplate: this.renderServiceGroupItemTemplate,
							onChange: this.onServiceChange,
							dataqa: 'return-label-service',
						}}
					/>
				</div>
				<span className={`p-col-2 cost-amount-span ${mobileClassName}`}>
					{usCurrencyFormatter.format(cost.service)}
				</span>
				{showReturnsDescription && (
					<div className="p-col-10">
						<div className="common-ui-element-wrapper">
							<div className="label-container" />
							<div
								className={`p-col-8 description-container ${mobileClassName}`}
								dangerouslySetInnerHTML={{
									__html: selectedServiceRate.packageTypeDescription,
								}}
							/>
						</div>
					</div>
				)}
				<div className={`p-col-10 ${mobileClassName}`}>
					<InsuranceFieldWithLabel
						label={insureForText}
						showErrorBelow={isMobile}
						uiElementConfig={{
							value: insureFor,
							disabled: carrier === Carrier.USPS && !isObject(insuranceOption),
							insuranceOption,
							onChange: this.onInsureForChange,
							dataqa: 'create-return-label-insure-for',
						}}
					/>
				</div>
				<span className={`p-col-2 cost-amount-span ${mobileClassName}`}>
					{usCurrencyFormatter.format(cost.insureFor)}
				</span>
				<div className={`p-col-10 ${mobileClassName}`}>
					<TrackingFieldWithLabel
						label="Tracking:"
						uiElementConfig={{
							name: 'tracking',
							placeholder: 'Select a Tracking Service',
							carrier,
							serviceType,
							packageType,
							value: tracking,
							onChange: this.onTrackingChange,
							disabled: isTrackingDisabled,
							dataqa: 'return-label-tracking',
						}}
					/>
				</div>
				<span className={`p-col-2 cost-amount-span ${mobileClassName}`}>
					{usCurrencyFormatter.format(cost.tracking || 0)}
				</span>
				<div className="p-col-12">
					<hr />
				</div>
				<div className={`p-col-10 ${mobileClassName}`}>
					<WrappedInputTextWithLabel
						label="RMA Memo:"
						uiElementConfig={{
							type: 'text',
							name: 'rmaMemo',
							maxLength: 42,
							placeholder: 'Optional',
							value: rmaMemo,
							onChange: this.onChange,
							dataqa: 'return-label-rma-memo',
						}}
					/>
				</div>
				{hasCostCodesCapability && (
					<>
						<div className={`p-col-10 ${mobileClassName}`}>
							<WrappedDropdownWithLabel
								label="Cost Code:"
								uiElementConfig={{
									name: 'costCode',
									placeholder: 'Any',
									options: [
										...costCodeOptions,
										{
											code: 'None',
											id: '',
										},
									],
									optionLabel: 'code',
									optionValue: 'id',
									value: costCode,
									onChange: this.onChange,
									dataqa: 'return-label-cost-code',
								}}
							/>
						</div>
					</>
				)}
				{FeeUtil.hasFeeCodes({
					costDetails,
					feeCodes: CostDetailsEnum[FeeType.PRINT],
				}) && (
					<FeeDetails
						costDetails={costDetails}
						showUpgradeModal={showUpgradeModal}
						renderAsAccordion
					/>
				)}

				<div className={`p-col-12 ${mobileClassName}`}>
					<div className="p-grid p-nogutter p-justify-end">
						<div className={`p-col-2 total ${mobileClassName}`}>
							<b>Total:</b>
						</div>
						<span className={`p-col-2 cost-amount-span ${mobileClassName}`}>
							<b>{usCurrencyFormatter.format(totalCost)}</b>
						</span>
					</div>
				</div>
			</div>
		);
	};

	renderSecondView = () => {
		const {
			layoutContext: { isMobile },
		} = this.props;
		const {
			emailReturnLabelOption,
			recipientEmail,
			notes,
			sendCopy,
			validationErrors,
		} = this.state;
		const { customerDetails = {} } = Session.getAccountInfo();
		const {
			address: { name = '' },
		} = customerDetails;
		const mobileClassName = isMobile ? 'mobile-wrapper' : '';

		return (
			<div className="p-grid p-nogutter">
				<div className={`p-col-12 ${mobileClassName}`}>
					<p>How do you want to email this return label?</p>
				</div>
				<div className={`p-col-12 ${mobileClassName}`}>
					<WrappedRadioButton
						inputId="stamps-email-return-label-option"
						name="emailReturnLabelOption"
						value={EmailReturnLabelOption.Stamps}
						onChange={this.onChange}
						checked={emailReturnLabelOption === EmailReturnLabelOption.Stamps}
						dataqa="stamps"
					>
						Use Stamps.com to email this label
					</WrappedRadioButton>
				</div>
				<div className={`p-col-12 ${mobileClassName}`}>
					<WrappedRadioButton
						inputId="customer-email-return-label-option"
						name="emailReturnLabelOption"
						value={EmailReturnLabelOption.Customer}
						onChange={this.onChange}
						checked={emailReturnLabelOption === EmailReturnLabelOption.Customer}
						dataqa="customer"
					>
						Use my own email application to send the label (does not work with
						web-based email)
					</WrappedRadioButton>
				</div>

				<div className="p-col-12">
					<hr />
				</div>
				<div className={`p-col-12 ${mobileClassName}`}>
					<div className="p-grid p-nogutter email-container">
						<div className={`p-col-2 email-label ${mobileClassName}`}>
							Email from:
						</div>
						<div className={`p-col-9 ${mobileClassName}`}>{name}</div>
					</div>
				</div>
				<div className={`p-col-12 ${mobileClassName}`}>
					<WrappedInputTextWithLabel
						label="Recipient Email:"
						errorMessage={
							recipientEmail === ''
								? 'Recipient Email is required.'
								: 'Invalid Email address.'
						}
						isInputInvalid={validationErrors.recipientEmail === -1}
						showErrorBelow={isMobile}
						uiElementConfig={{
							type: 'text',
							name: 'recipientEmail',
							maxLength: 42,
							value: recipientEmail,
							onChange: this.onChange,
							dataqa: 'return-label-recipient-email',
						}}
					/>
				</div>
				<div className={`p-col-12 ${mobileClassName}`}>
					<WrappedInputTextareaWithLabel
						label="Notes:"
						uiElementConfig={{
							rows: 5,
							cols: 31,
							maxLength: 200,
							value: notes,
							onChange: this.onChange,
							name: 'notes',
							placeholder: 'Optional',
							dataqa: 'return-label-notes',
						}}
					/>
				</div>
				{emailReturnLabelOption === EmailReturnLabelOption.Stamps && (
					<WrappedCheckbox
						name="sendCopy"
						checked={sendCopy}
						dataqa="return-label-send-copy"
						onChange={this.onChange}
					>
						Send me a copy of this email
					</WrappedCheckbox>
				)}
			</div>
		);
	};

	renderNoLabelDropOffView = isNoLabelDropOffSelected => {
		const {
			layoutContext: { isMobile },
		} = this.props;
		const {
			emailReturnLabelOption,
			recipientEmail,
			notes,
			sendCopy,
			validationErrors,
			userEmail,
		} = this.state;
		const { customerDetails = {} } = Session.getAccountInfo();
		const {
			address: { name = '' },
		} = customerDetails;
		const mobileClassName = isMobile ? 'mobile-wrapper' : '';

		return (
			<div className="p-grid p-nogutter">
				<div className={`p-col-12 ${mobileClassName}`}>
					<p>How do you want to email the QR code and return instructions</p>
				</div>
				<div className={`p-col-12 ${mobileClassName}`}>
					<WrappedRadioButton
						name="emailReturnLabelOption"
						inputId="stamps-email-return-label-option-no-label-drop-off"
						value={EmailReturnLabelOption.Stamps}
						onChange={this.onChange}
						checked={emailReturnLabelOption === EmailReturnLabelOption.Stamps}
						dataqa="stamps"
					>
						Use Stamps.com to email this label
					</WrappedRadioButton>
				</div>
				<div className={`p-col-12 ${mobileClassName} noLabelDropOffSelect`}>
					<WrappedRadioButton
						name="emailReturnLabelOption"
						inputId="customer-email-return-label-option-no-label-drop-off"
						value={EmailReturnLabelOption.Customer}
						onChange={this.onChange}
						checked={emailReturnLabelOption === EmailReturnLabelOption.Customer}
						dataqa="customer"
						disabled={isNoLabelDropOffSelected}
					>
						Use my own email application to send the label (does not work with
						web-based email)
					</WrappedRadioButton>
				</div>

				<div className="p-col-12">
					<hr />
				</div>
				<div className={`p-col-12 ${mobileClassName}`}>
					<div className="p-grid p-nogutter email-container">
						<div className={`p-col-2 email-label ${mobileClassName}`}>
							Email from:
						</div>
						<div className={`p-col-9 ${mobileClassName}`}>{name}</div>
					</div>
				</div>
				<div className={`p-col-12 ${mobileClassName}`}>
					<WrappedInputTextWithLabel
						label="Recipient Email:"
						errorMessage={
							recipientEmail === ''
								? 'Recipient Email is required.'
								: 'Invalid Email address.'
						}
						isInputInvalid={validationErrors.recipientEmail === -1}
						showErrorBelow={isMobile}
						uiElementConfig={{
							type: 'text',
							name: 'recipientEmail',
							maxLength: 42,
							value: recipientEmail,
							onChange: this.onChange,
							dataqa: 'return-label-recipient-email',
						}}
					/>
				</div>
				<div className={`p-col-12 ${mobileClassName}`}>
					<WrappedInputTextareaWithLabel
						label="Notes:"
						uiElementConfig={{
							rows: 5,
							cols: 31,
							maxLength: 200,
							value: notes,
							onChange: this.onChange,
							name: 'notes',
							placeholder: 'Optional',
							dataqa: 'return-label-notes',
						}}
					/>
				</div>
				{emailReturnLabelOption === EmailReturnLabelOption.Stamps && (
					<WrappedCheckbox
						name="sendCopy"
						checked={sendCopy}
						dataqa="return-label-send-copy"
						onChange={this.onChange}
					>
						Email a copy to: {userEmail} &nbsp;
						<a
							href={null}
							role="button"
							tabIndex="0"
							onClick={() => {
								this.onEditUserEmailButtonClick(() => {
									this.setState({ sendCopy: true });
								});
							}}
						>
							Edit
						</a>
					</WrappedCheckbox>
				)}
			</div>
		);
	};

	render() {
		const { modalId } = this.props;
		const {
			returnLabelFormView,
			emailReturnLabelOption,
			isLoading,
			loadingMessage,
			imageURL,
			service,
		} = this.state;

		const returnLabelPackageStr = 'Drop-Off: No Label Needed';
		const isNoLabelDropOffSelected = service === returnLabelPackageStr;

		const renderSwitchView = isNoLabelDropOffSelected
			? this.renderNoLabelDropOffView(isNoLabelDropOffSelected)
			: this.renderSecondView();

		const buttons = {
			primaryButton: returnLabelFormView
				? {
						disabled: isLoading === true,
						type: ModalButtonActionType.Ok,
						text: 'Continue',
						onClick: this.onContinueButtonClick,
						dataqa: 'create-return-label-continue-button',
					}
				: {
						disabled: isLoading === true,
						type: ModalButtonActionType.Ok,
						text: 'Send',
						onClick: this.onSendButtonClick,
						dataqa: 'create-return-label-send-button',
					},

			secondaryButton:
				!returnLabelFormView &&
				emailReturnLabelOption === EmailReturnLabelOption.Stamps
					? {
							disabled: isLoading === true,
							type: ModalButtonActionType.Ok,
							text: 'Preview',
							onClick: this.onPreviewButtonClick,
							dataqa: 'create-return-label-preview-button',
						}
					: null,
		};

		return (
			<Modal
				modalId={modalId}
				header={
					isNoLabelDropOffSelected && !returnLabelFormView
						? 'Drop-Off: No Label Needed'
						: 'Return Label'
				}
				className={`history-return-label create-return-label-modal ${
					isLoading === true ? 'loading' : ''
				}`}
				primaryButton={buttons.primaryButton}
				secondaryButton={buttons.secondaryButton}
				size="medium"
			>
				{returnLabelFormView ? this.renderFirstView() : renderSwitchView}
				{imageURL && (
					<PrintContent imageURL={imageURL} onAfterPrint={this.onAfterPrint} />
				)}
				{isLoading === true ? <Spinner msg={loadingMessage} /> : null}
			</Modal>
		);
	}
}

CreateReturnLabelModal.propTypes = {
	data: PropTypes.shape({
		costCodeOptions: PropTypes.array,
		selectedPrints: PropTypes.array,
		insuranceOption: PropTypes.object,
	}),
};

export default withLayoutAndModalContexts(CreateReturnLabelModal);
